/**
 * Pure presentation helpers for `RaidCalendarView`. All functions are
 * stateless and Vue-free, so the same helpers can be unit-tested with
 * `node --test` without dragging in Pinia / Apollo / Vue.
 *
 * Three buckets live here:
 *
 *   1. `Intl.DateTimeFormat` date labels (`weekday`, `dayNumber`,
 *      `fullDate`, `timeLabel`, `isToday`).
 *   2. Raid-status presentation (`statusLabel`, `statusClass` table).
 *   3. Calendar grid geometry (`formatHour`, `hourLabelStyle`,
 *      `hourLineStyle`, `raidBlockStyle`, plus the `*_PX` constants they
 *      scale by).
 *
 * The grid constants are published so future callers (e.g. a "compact"
 * day-view at half height) can derive their own styles without
 * re-declaring the magic number `15` — every position-style helper in
 * this module reads them. The vertical axis is intentionally inverted:
 * late-night time is at the top and midnight is at the bottom.
 */

import type { AppLocale } from '@/shared/i18n'
import { RaidStatus } from '../types/index.ts'
import type { RaidId, RaidStatus as RaidStatusType, RaidSummary } from '../types/index.ts'
import { calendarEn } from '../components/calendar/locales/en.ts'
import { calendarRu } from '../components/calendar/locales/ru.ts'

const CALENDAR_MESSAGES = { en: calendarEn, ru: calendarRu } as const

function messages(locale: AppLocale): typeof calendarEn {
  return CALENDAR_MESSAGES[locale]
}

/** Pixels per 15-min slot in the compact calendar. */
export const CALENDAR_SLOT_HEIGHT_PX = 8
export const CALENDAR_SLOT_MINUTES = 15
export const CALENDAR_SLOTS_PER_HOUR = 4
export const CALENDAR_SLOTS_PER_DAY = 24 * CALENDAR_SLOTS_PER_HOUR
export const CALENDAR_DAY_HEIGHT_PX = CALENDAR_SLOTS_PER_DAY * CALENDAR_SLOT_HEIGHT_PX

// ── Date / time labels ────────────────────────────────────────────────

/** Short weekday label, locale-aware ("Mon", "Tue", …). */
export function weekday(date: Date, locale: AppLocale = 'en'): string {
  return new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date)
}

/** Day-of-month + month abbrev, e.g. "15 Jul". */
export function dayNumber(date: Date, locale: AppLocale = 'en'): string {
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(date)
}

/** Day of month for the compact, Google Calendar-style header badge. */
export function dayOfMonth(date: Date, locale: AppLocale = 'en'): string {
  return new Intl.DateTimeFormat(locale, { day: 'numeric' }).format(date)
}

/** Day + month + year, e.g. "15 Jul 2026". */
export function fullDate(date: Date, locale: AppLocale = 'en'): string {
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date)
}

/** Localised 24-hour time-of-day for a raid. */
export function timeLabel(raid: RaidSummary, locale: AppLocale = 'en'): string {
  if (raid.scheduledStartsAt === null) return messages(locale).formatters.unscheduled
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(raid.scheduledStartsAt))
}

/** Same Y/M/D as `today` in the local timezone. */
export function isToday(date: Date, today: Date): boolean {
  return date.getFullYear() === today.getFullYear()
    && date.getMonth() === today.getMonth()
    && date.getDate() === today.getDate()
}

// ── Raid-status presentation ──────────────────────────────────────────

/** UI label for a raid status: `'in_progress'` → `'in progress'`. */
export function statusLabel(status: RaidStatusType, locale: AppLocale = 'en'): string {
  const catalog = messages(locale).formatters
  if (status === RaidStatus.Scheduled) return catalog.statusScheduled
  if (status === RaidStatus.InProgress) return catalog.statusInProgress
  if (status === RaidStatus.Completed) return catalog.statusCompleted
  if (status === RaidStatus.Cancelled) return catalog.statusCancelled
  return catalog.statusOverdue
}

/**
 * Tailwind utility class string for each `RaidStatus`. Exposed as a frozen
 * `Readonly<Record<...>>` so the calendar agent (and any future view that
 * surfaces raid chips) can look up the styling without re-deriving it.
 *
 * Colors track the planner chip strip — `info` for scheduled, `success` for
 * in-progress, muted for completed, `danger` for cancelled (struck through),
 * `warning` for overdue.
 */
export const statusClass: Readonly<Record<RaidStatusType, string>> = Object.freeze({
  [RaidStatus.Scheduled]: 'border-info/50 bg-info/10 text-info',
  [RaidStatus.InProgress]: 'border-success/60 bg-success/15 text-success',
  [RaidStatus.Completed]: 'border-border-strong bg-bg-elevated text-text-muted',
  [RaidStatus.Cancelled]: 'border-danger/40 bg-danger/10 text-danger line-through',
  [RaidStatus.Overdue]: 'border-warning/60 bg-warning/10 text-warning',
})

/**
 * Semantic left-border color for the calendar's full-duration rail. The rail
 * is decorative; the raid button's accessible name carries the same status
 * and duration information for assistive technology.
 */
export const statusRailClass: Readonly<Record<RaidStatusType, string>> = Object.freeze({
  [RaidStatus.Scheduled]: 'border-l-info',
  [RaidStatus.InProgress]: 'border-l-success',
  [RaidStatus.Completed]: 'border-l-border-strong',
  [RaidStatus.Cancelled]: 'border-l-danger',
  [RaidStatus.Overdue]: 'border-l-warning',
})

/**
 * Calendar-tile colors are driven by the explicit publication state, while
 * the duration rail continues to communicate the time-derived raid status.
 * This keeps scheduled published and unpublished raids visibly distinct.
 */
export function publicationClass(raid: RaidSummary): string {
  return raid.isPublished
    ? 'border-success/60 bg-success/15 text-success'
    : 'border-warning/60 bg-warning/10 text-warning'
}

// ── Calendar grid geometry ────────────────────────────────────────────

/** `"HH:00"` string for a 1-based inverted hour index in `[1..25]`. */
export function formatHour(hour: number): string {
  const h = Math.max(0, Math.min(24, 25 - hour))
  return `${String(h).padStart(2, '0')}:00`
}

/** Local 24-hour label for a 15-minute grid slot, e.g. `16:15`. */
export function slotLabel(minutes: number): string {
  const clamped = Math.max(0, Math.min(23 * 60 + 45, minutes))
  const hour = Math.floor(clamped / 60)
  const minute = clamped % 60
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

function snappedSlotMinutes(minutes: number): number {
  return Math.max(
    0,
    Math.min(
      (CALENDAR_SLOTS_PER_DAY - 1) * CALENDAR_SLOT_MINUTES,
      Math.floor(minutes / CALENDAR_SLOT_MINUTES) * CALENDAR_SLOT_MINUTES,
    ),
  )
}

function pixelsForMinutes(minutes: number): number {
  return (minutes / CALENDAR_SLOT_MINUTES) * CALENDAR_SLOT_HEIGHT_PX
}

/** Convert an inverted day-column Y coordinate into its 15-minute time slot. */
export function slotMinutesAtCalendarY(y: number): number {
  const slotFromTop = Math.max(
    0,
    Math.min(CALENDAR_SLOTS_PER_DAY - 1, Math.floor(y / CALENDAR_SLOT_HEIGHT_PX)),
  )
  return (CALENDAR_SLOTS_PER_DAY - 1 - slotFromTop) * CALENDAR_SLOT_MINUTES
}

/** Absolute geometry for one inverted 15-minute time slot. */
export function slotStyle(minutes: number): Record<string, string> {
  const snapped = snappedSlotMinutes(minutes)
  return {
    top: `${CALENDAR_DAY_HEIGHT_PX - pixelsForMinutes(snapped) - CALENDAR_SLOT_HEIGHT_PX}px`,
    height: `${CALENDAR_SLOT_HEIGHT_PX}px`,
  }
}

/** Absolute-position style for the hour label rendered in the time ruler.
 * 1-based `hour` in `[1..25]`; the row sequence runs 24:00 → 00:00. */
export function hourLabelStyle(hour: number): Record<string, string> {
  const top = (hour - 1) * CALENDAR_SLOTS_PER_HOUR * CALENDAR_SLOT_HEIGHT_PX
  return {
    position: 'absolute',
    top: `${top}px`,
    right: '0.5rem',
    left: '0',
    ...(hour === 25 ? { transform: 'translateY(-100%)' } : {}),
  }
}

/** Absolute-position style for the horizontal grid line that separates
 * one inverted hour from the next. */
export function hourLineStyle(hour: number): Record<string, string> {
  const top = (hour - 1) * CALENDAR_SLOTS_PER_HOUR * CALENDAR_SLOT_HEIGHT_PX
  return { top: `${top}px`, height: '0px' }
}

/** Absolute-position style for the live "now" marker in today's column. */
export function nowLineStyle(now: Date): Record<string, string> {
  const minutes = now.getHours() * 60 + now.getMinutes()
  return { top: `${CALENDAR_DAY_HEIGHT_PX - pixelsForMinutes(minutes)}px` }
}

/**
 * Compute the absolute position of a raid block within its day column. The
 * column is `CALENDAR_DAY_HEIGHT_PX` tall; the raid's bottom edge is its
 * start time and its height grows upward through later time. This is the same
 * inverted coordinate system used by the hour ruler and pointer placement.
 * Skips blocks with no scheduled start — those should not render on the time
 * grid at all.
 */
export function raidBlockStyle(raid: RaidSummary): Record<string, string> {
  if (raid.scheduledStartsAt === null) {
    return { display: 'none' }
  }
  const start = new Date(raid.scheduledStartsAt)
  const startMinutes = snappedSlotMinutes(start.getHours() * 60 + start.getMinutes())
  const duration = Math.max(15, raid.durationMinutes ?? 30)
  const visibleDuration = Math.min(24 * 60 - startMinutes, duration)
  const heightPx = pixelsForMinutes(visibleDuration)
  const topPx = CALENDAR_DAY_HEIGHT_PX - pixelsForMinutes(startMinutes + visibleDuration)
  return { top: `${topPx}px`, height: `${heightPx}px` }
}

interface RaidInterval {
  readonly raid: RaidSummary
  readonly start: number
  readonly end: number
}

interface RaidColumn {
  readonly interval: RaidInterval
  readonly index: number
}

function raidInterval(raid: RaidSummary): RaidInterval | null {
  if (raid.scheduledStartsAt === null) return null
  const start = new Date(raid.scheduledStartsAt)
  if (!Number.isFinite(start.getTime())) return null
  const startMinutes = start.getHours() * 60 + start.getMinutes()
  return {
    raid,
    start: startMinutes,
    end: startMinutes + Math.max(15, raid.durationMinutes ?? 30),
  }
}

/**
 * Lay out simultaneous raids in separate columns, matching Google Calendar's
 * collision behaviour. A connected overlap cluster shares its available
 * width; a raid without a collision keeps the full day-column width.
 */
export function raidBlockStyles(
  raids: ReadonlyArray<RaidSummary>,
): ReadonlyMap<RaidId, Readonly<Record<string, string>>> {
  const intervals: ReadonlyArray<RaidInterval> = [...raids
    .map(raidInterval)
    .filter((value): value is RaidInterval => value !== null)]
    .sort((a, b) => a.start - b.start || a.end - b.end || a.raid.id.localeCompare(b.raid.id))
  const styles = new Map<RaidId, Readonly<Record<string, string>>>()
  let cursor = 0

  while (cursor < intervals.length) {
    const first = intervals[cursor]
    if (first === undefined) break
    let cluster: ReadonlyArray<RaidInterval> = [first]
    let clusterEnd = first.end
    cursor += 1

    while (cursor < intervals.length) {
      const next = intervals[cursor]
      if (next === undefined || next.start >= clusterEnd) break
      cluster = [...cluster, next]
      clusterEnd = Math.max(clusterEnd, next.end)
      cursor += 1
    }

    let columns: ReadonlyArray<RaidColumn> = []
    let occupiedUntil: ReadonlyArray<number> = []
    for (const interval of cluster) {
      const available = occupiedUntil.findIndex((end) => end <= interval.start)
      const index = available === -1 ? occupiedUntil.length : available
      occupiedUntil = occupiedUntil.map((end, columnIndex) =>
        columnIndex === index ? interval.end : end,
      )
      if (available === -1) occupiedUntil = [...occupiedUntil, interval.end]
      columns = [...columns, { interval, index }]
    }

    const columnCount = Math.max(1, occupiedUntil.length)
    for (const column of columns) {
      const base = raidBlockStyle(column.interval.raid)
      const width = 100 / columnCount
      styles.set(column.interval.raid.id, {
        ...base,
        left: `calc(${String(column.index * width)}% + 0.25rem)`,
        width: `calc(${String(width)}% - 0.5rem)`,
      })
    }
  }

  return styles
}

// ── Screen-reader labels ──────────────────────────────────────────────

/** `aria-label` for a day column header: full date + planned-raid count. */
export function dayAriaLabel(
  date: Date,
  count: number,
  locale: AppLocale = 'en',
): string {
  return messages(locale).formatters.plannedRaidCount(fullDate(date, locale), count)
}

/** `aria-label` for a raid block: name + time + duration + status + publication text. */
export function raidAriaLabel(
  raid: RaidSummary,
  status: RaidStatusType,
  locale: AppLocale = 'en',
): string {
  const catalog = messages(locale).formatters
  const publication = raid.isPublished ? catalog.published : catalog.unpublished
  return catalog.raidAria(
    raid.name,
    timeLabel(raid, locale),
    raid.durationMinutes ?? 30,
    statusLabel(status, locale),
    publication,
  )
}